import { useEffect, useState } from 'react'
import { Alert, Code, Loader, Tabs } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { GenieQueryVisualization } from '@databricks/appkit-ui/react'
import appkitStyles from '@databricks/appkit-ui/styles.css?raw'
import { ShadowScope } from '../../components/common/ShadowScope'
import {
  getGenieAppkitStatementResponse,
  type GenieAppkitStatementResponse,
} from '../../api/genie'

interface GenieQueryResultViewProps {
  statementId: string
  sql?: string
}

export function GenieQueryResultView({
  statementId,
  sql,
}: GenieQueryResultViewProps) {
  const [data, setData] = useState<GenieAppkitStatementResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)

    getGenieAppkitStatementResponse(statementId)
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load query result.'
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [statementId])

  return (
    <Tabs defaultValue="result" mt="xs" variant="outline">
      <Tabs.List>
        <Tabs.Tab value="result">Result</Tabs.Tab>
        {sql && <Tabs.Tab value="sql">SQL</Tabs.Tab>}
      </Tabs.List>

      <Tabs.Panel value="result" pt="xs">
        {loading && <Loader size="sm" mt="xs" />}
        {error && (
          <Alert icon={<IconAlertCircle size={14} />} color="red" mt="xs">
            {error}
          </Alert>
        )}
        {data && (
          <ShadowScope styles={appkitStyles}>
            <GenieQueryVisualization data={data} />
          </ShadowScope>
        )}
      </Tabs.Panel>

      {sql && (
        <Tabs.Panel value="sql" pt="xs">
          <Code block style={{ whiteSpace: 'pre-wrap' }}>
            {sql}
          </Code>
        </Tabs.Panel>
      )}
    </Tabs>
  )
}
